import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {Text, TouchableOpacity, View, Image, ScrollView} from 'react-native';
import Modal from 'react-native-modal';
import {createStyles} from '../../utils/createStyles';
import LinearGradient from 'react-native-linear-gradient';
import {convertCssGradientToLinearGradient} from '../../utils/utils';
import {Stage, Term, Course} from '../../API/newHome';
import Toast from 'react-native-toast-message';

// 在文件顶部添加工具函数
const getTextLength = (text: string): number => {
  return text.replace(/[\x00-\xff]/g, '01').length;
};

// 添加新的接口定义
export interface VersionInfo {
  stage_id: number;
  subject_id: number;
  course_id: number;
  version_id: number;
}

export interface CurriculumSetup {
  xkwStageId: number;
  xkwGradeId: number;
  xkwTerm: string;
  xkwVersionList: VersionInfo[];
}

// 修改 props 接口
export interface GradeSelectorProps {
  isVisible: boolean;
  onClose: () => void;
  onChange: (curriculumSetup: CurriculumSetup) => void;
  stages: Stage[];
  terms: Term[];
  initialValues?: CurriculumSetup;
}

// 添加 memo 包裹子组件
const TermSection = React.memo(
  ({
    terms,
    selectedTerm,
    setSelectedTerm,
  }: {
    terms: Term[];
    selectedTerm: string;
    setSelectedTerm: (term: string) => void;
  }) => (
    <View style={styles.section}>
      <View style={styles.sectionTitleWrap}>
        <View style={styles.titleDecoration} />
        <Text style={styles.sectionTitle}>选择学期</Text>
      </View>
      <View style={styles.optionsWrap}>
        {terms.map(term => {
          const isLongText = getTextLength(term.name) > 6;
          return (
            <TouchableOpacity
              key={term.id}
              style={[
                styles.termItem,
                isLongText ? styles.itemLong : styles.itemShort,
                selectedTerm === String(term.id) && styles.itemActive,
              ]}
              onPress={() => setSelectedTerm(String(term.id))}>
              <Text
                style={[
                  styles.textStyle,
                  selectedTerm === String(term.id) && styles.textActive,
                ]}
                numberOfLines={1}>
                {term.name}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  ),
);

// 优化主组件
const GradeSelector: React.FC<GradeSelectorProps> = React.memo(
  ({isVisible, onClose, onChange, stages, terms, initialValues}) => {
    // 修改 selectedGrade 的初始化，只用 initialValues
    const [selectedGrade, setSelectedGrade] = useState<number | undefined>(
      initialValues?.xkwGradeId,
    );
    const [selectedTerm, setSelectedTerm] = useState<string>(
      initialValues?.xkwTerm || '',
    );
    const [selectedStage, setSelectedStage] = useState<Stage>();
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedVersions, setSelectedVersions] = useState<{
      [key: number]: number;
    }>(
      // 将 xkwVersionList 转换为 selectedVersions 格式
      initialValues?.xkwVersionList?.reduce((acc, item) => {
        acc[item.course_id] = item.version_id;
        return acc;
      }, {} as {[key: number]: number}) || {},
    );

    // 添加展开状态的管理
    const [expandedCourses, setExpandedCourses] = useState<{
      [key: number]: boolean;
    }>({});

    // 修改初始化逻辑
    useEffect(() => {
      // 如果有初始值，使用初始值
      if (initialValues && initialValues.xkwGradeId) {
        setSelectedGrade(Number(initialValues.xkwGradeId));
        setSelectedTerm(String(initialValues.xkwTerm));

        const stage = stages.find(s =>
          s.grades.some(g => Number(g.id) === Number(initialValues.xkwGradeId)),
        );
        setSelectedStage(stage);

        if (stage) {
          setCourses(stage.courses);
          const versions = initialValues.xkwVersionList.reduce((acc, item) => {
            const course = stage.courses.find(
              c => Number(c.subject.id) === Number(item.subject_id),
            );
            if (course) {
              acc[Number(course.id)] = Number(item.version_id);
            }
            return acc;
          }, {} as {[key: number]: number});

          setSelectedVersions(versions);
        }
      } else if (stages.length > 0) {
        // 没有初始值时，设置默认值
        const firstGrade = stages[0].grades[0];
        if (firstGrade) {
          setSelectedGrade(Number(firstGrade.id));
          setSelectedStage(stages[0]);
          const stageCourses = stages[0].courses;
          setCourses(stageCourses);

          // 为每个课程选择第一个版本
          const defaultVersions = stageCourses.reduce((acc, course) => {
            if (course.versions.length > 0) {
              acc[course.id] = course.versions[0].id;
            }
            return acc;
          }, {} as {[key: number]: number});

          setSelectedVersions(defaultVersions);
        }

        if (terms.length > 0) {
          setSelectedTerm(String(terms[0].id));
        }
      }
    }, [initialValues, stages, terms]);

    const handleSave = useCallback(() => {
      // 验证选择
      if (!selectedTerm) {
        Toast.show({
          type: 'error',
          text1: '请选择学期',
        });
        return;
      }

      if (!selectedGrade) {
        Toast.show({
          type: 'error',
          text1: '请选择年级',
        });
        return;
      }

      if (courses.length > 0) {
        const allVersionsSelected = courses.every(
          course => selectedVersions[course.id],
        );
        if (!allVersionsSelected) {
          Toast.show({
            type: 'error',
            text1: '请为所有学科选择版本',
          });
          return;
        }
      }

      // 构建 curriculumSetup 数据
      const curriculumSetup: CurriculumSetup = {
        xkwStageId: selectedStage?.id || 0,
        xkwGradeId: selectedGrade || 0,
        xkwTerm: selectedTerm || '',
        xkwVersionList: courses.map(course => ({
          stage_id: selectedStage?.id || 0,
          subject_id: course.subject.id,
          course_id: course.id,
          version_id: selectedVersions[course.id] || 0,
        })),
      };

      // 直接调用 onChange 和 onClose
      onChange(curriculumSetup);
      onClose();
    }, [
      selectedTerm,
      selectedGrade,
      courses,
      selectedVersions,
      selectedStage,
      onChange,
      onClose,
    ]);

    // 修改年级选择的点击处理
    const handleGradeSelect = useCallback(
      (gradeId: string) => {
        const newGradeId = Number(gradeId);
        setSelectedGrade(newGradeId);

        const stage = stages.find(s =>
          s.grades.some(g => Number(g.id) === newGradeId),
        );

        if (stage) {
          setSelectedStage(stage);
          setCourses(stage.courses);

          const defaultVersions = stage.courses.reduce((acc, course) => {
            if (course.versions.length > 0) {
              acc[course.id] = course.versions[0].id;
            }
            return acc;
          }, {} as {[key: number]: number});

          setSelectedVersions(defaultVersions);
        }
      },
      [stages],
    );

    // 使用 useCallback 优化回调函数
    const handleTermSelect = useCallback((termId: string) => {
      setSelectedTerm(termId);
    }, []);

    // 修改 handleVersionSelect
    const handleVersionSelect = useCallback(
      (courseId: number, versionId: number) => {
        setSelectedVersions(prev => ({
          ...prev,
          [courseId]: versionId,
        }));
      },
      [],
    );

    const handleExpandToggle = useCallback((courseId: number) => {
      setExpandedCourses(prev => ({
        ...prev,
        [courseId]: !prev[courseId],
      }));
    }, []);

    // 使用 useMemo 优化计算属性
    const courseSections = useMemo(() => {
      if (!selectedStage || !courses.length) {
        return null;
      }

      return courses.map(course => (
        <View key={course.id} style={styles.section}>
          <View style={styles.sectionTitleWrap}>
            <View style={styles.titleDecoration} />
            <Text style={styles.sectionTitle}>{course.subject.name}</Text>
          </View>
          <View style={styles.optionsWrap}>
            {(expandedCourses[course.id]
              ? course.versions
              : course.versions.slice(0, 11)
            ).map(version => {
              const isLongText = getTextLength(version.name) > 6;
              return (
                <TouchableOpacity
                  key={version.id}
                  style={[
                    styles.versionItem,
                    isLongText ? styles.itemLong : styles.itemShort,
                    Number(selectedVersions[Number(course.id)]) ===
                      Number(version.id) && styles.itemActive,
                  ]}
                  onPress={() =>
                    handleVersionSelect(Number(course.id), Number(version.id))
                  }>
                  <Text
                    style={[
                      styles.textStyle,
                      Number(selectedVersions[Number(course.id)]) ===
                        Number(version.id) && styles.textActive,
                    ]}
                    numberOfLines={1}>
                    {version.name}
                  </Text>
                </TouchableOpacity>
              );
            })}
            {course.versions.length > 11 && (
              <TouchableOpacity
                style={[
                  styles.versionItem,
                  styles.expandButton,
                  styles.itemShort,
                ]}
                onPress={() => handleExpandToggle(Number(course.id))}>
                {expandedCourses[course.id] ? (
                  <Image
                    style={styles.expandIcon}
                    source={{
                      uri: 'https://media.oss.leziedu.com/webapp/classroom/newPad/grade_shou.png',
                    }}
                  />
                ) : (
                  <Image
                    style={styles.expandIcon}
                    source={{
                      uri: 'https://media.oss.leziedu.com/webapp/classroom/newPad/grade_kai.png',
                    }}
                  />
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      ));
    }, [
      selectedStage,
      courses,
      selectedVersions,
      expandedCourses,
      handleVersionSelect,
      handleExpandToggle,
    ]);

    return (
      <Modal
        isVisible={isVisible}
        onBackdropPress={() => {}}
        backdropTransitionOutTiming={0}
        useNativeDriver={true}
        hideModalContentWhileAnimating={true}
        style={styles.modal}>
        <View style={styles.container}>
          <View style={styles.modalContent}>
            <View style={styles.header}>
              <Text style={styles.modalTitle}>选择年级</Text>
              <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                <Image
                  style={styles.closeIcon}
                  source={{
                    uri: 'https://media.oss.leziedu.com/webapp/classroom/close.png',
                  }}
                />
              </TouchableOpacity>
            </View>

            <ScrollView
              style={styles.scrollContent}
              showsVerticalScrollIndicator={false}
              removeClippedSubviews={true}>
              <TermSection
                terms={terms}
                selectedTerm={selectedTerm}
                setSelectedTerm={handleTermSelect}
              />

              {/* 年级选择 */}
              <View style={styles.section}>
                <View style={styles.sectionTitleWrap}>
                  <View style={styles.titleDecoration} />
                  <Text style={styles.sectionTitle}>选择年级</Text>
                </View>
                <View style={styles.optionsWrap}>
                  {stages.map(stage =>
                    stage.grades.map(grade => {
                      const isLongText = getTextLength(grade.name) > 6;
                      return (
                        <TouchableOpacity
                          key={grade.id}
                          style={[
                            styles.gradeItem,
                            isLongText ? styles.itemLong : styles.itemShort,
                            selectedGrade === Number(grade.id) &&
                              styles.itemActive,
                          ]}
                          onPress={() => handleGradeSelect(grade.id)}>
                          <Text
                            style={[
                              styles.textStyle,
                              selectedGrade === Number(grade.id) &&
                                styles.textActive,
                            ]}
                            numberOfLines={1}>
                            {grade.name}
                          </Text>
                        </TouchableOpacity>
                      );
                    }),
                  )}
                </View>
              </View>

              {courseSections}
            </ScrollView>
          </View>
          <View style={styles.buttonContainer}>
            <TouchableOpacity style={styles.button} onPress={handleSave}>
              <LinearGradient
                style={styles.button}
                {...convertCssGradientToLinearGradient(
                  'linear-gradient(171deg, #9E99FF -60%, #6455FF 89%);',
                )}>
                <Text style={[styles.buttonText, {color: '#fff'}]}>保存</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  },
);

const styles = createStyles({
  modal: {
    margin: 0,
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingLeft: '13rem',
  },
  container: {
    width: '72.9rem',
    maxWidth: '72.9rem',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '1rem',
    height: '40rem',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    height: '4.35rem',
    paddingHorizontal: '2rem',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalTitle: {
    fontSize: '1.5rem',
    color: '#3D3D3D',
    fontWeight: 'bold',
  },
  closeButton: {
    position: 'absolute',
    right: '2rem',
    width: '2.4rem',
    height: '2.4rem',
    zIndex: 1,
  },
  closeIcon: {
    width: '2.4rem',
    height: '2.4rem',
  },
  scrollContent: {
    flex: 1,
    paddingHorizontal: '3.33rem',
  },
  section: {
    marginBottom: '2.3rem', // 修改间距为 2.3rem
  },
  sectionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    color: '#666666',
    fontWeight: '500',
    height: '3rem',
    lineHeight: '3rem',
  },
  optionsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  termGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  gradeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  versionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  termItem: {
    height: '3rem',
    backgroundColor: '#F6F9FF',
    borderRadius: '0.6rem',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '1rem',
    marginBottom: '0.8rem',
  },
  gradeItem: {
    height: '3rem',
    backgroundColor: '#F6F9FF',
    borderRadius: '0.6rem',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '1rem',
    marginBottom: '0.8rem',
  },
  versionItem: {
    height: '3rem',
    backgroundColor: '#F6F9FF',
    borderRadius: '0.6rem',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '1rem',
    marginBottom: '0.8rem',
  },
  itemActive: {
    backgroundColor: '#8378F9', // 修改选中背景色
  },
  termText: {
    fontSize: '1.15rem', // 修改字体大小
    color: '#666666', // 修改字体颜色
  },
  gradeItemText: {
    fontSize: '1.15rem', // 修改字体大小
    color: '#666666', // 修改字体颜色
  },
  versionText: {
    fontWeight: '350',
    fontSize: '1.15rem', // 修改字体大小
    color: '#666666', // 修改字体颜色
  },
  textActive: {
    color: '#FFFFFF',
    fontWeight: '500', // 添加选中状态的字体粗细
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: '3.6rem',
  },
  button: {
    width: '32rem',
    height: '4.5rem',
    borderRadius: '3.25rem',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonText: {
    fontSize: '1.4rem',
  },
  sectionTitleWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  titleDecoration: {
    width: '0.25rem',
    height: '1.35rem',
    backgroundColor: '#9B92F9',
    marginRight: '0.5rem',
  },
  expandButton: {
    paddingHorizontal: '2rem',
    height: '3rem',
    backgroundColor: '#F6F9FF',
    borderRadius: '0.6rem',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '1rem',
    marginBottom: '0.8rem',
  },
  expandButtonText: {
    fontSize: '1.15rem',
    color: '#666666',
  },
  itemShort: {
    width: '8.7rem',
  },
  itemLong: {
    width: '18.1rem',
    paddingHorizontal: '1rem',
  },
  textStyle: {
    fontSize: '1.15rem',
    color: '#666666',
  },
  expandIcon: {
    width: '1.4rem',
    height: '1.4rem',
  },
});

export default GradeSelector;
